import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { ShieldCheck } from "lucide-react";

const TwoFactorChallenge = () => {
  const { loginWith2FA, isLoading } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const tempToken = location.state?.tempToken;

  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    // If navigated here without a tempToken, redirect to login
    if (!tempToken) {
      navigate("/login", { replace: true });
    } else {
      inputRef.current?.focus();
    }
  }, [tempToken, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const result = await loginWith2FA(tempToken, code.trim());
    if (result.success) {
      navigate("/dashboard", { replace: true });
    } else {
      setError(result.error);
      setCode("");
      inputRef.current?.focus();
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#020617] text-white">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
        <div className="absolute -top-32 right-[-10%] h-96 w-96 rounded-full bg-cyan-500/30 blur-[120px]" />
        <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-blue-500/20 blur-[120px]" />
        <div className="absolute inset-0 opacity-20">
          <div className="[background:radial-gradient(circle_at_1px,_rgba(255,255,255,0.25)_1px,_transparent_0)] h-full w-full [background-size:40px_40px]" />
        </div>
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-md rounded-3xl border border-white/10 bg-slate-900/70 p-8 shadow-2xl shadow-cyan-500/20 backdrop-blur-xl">
          <div className="flex flex-col items-center space-y-2 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-400/20 to-indigo-500/20 ring-1 ring-white/10">
              <ShieldCheck className="h-7 w-7 text-cyan-300" />
            </div>
            <p className="text-xs uppercase tracking-[0.4em] text-cyan-200/70">
              {t("twoFactor.challenge.eyebrow")}
            </p>
            <h2 className="text-2xl font-semibold text-white">
              {t("twoFactor.challenge.title")}
            </h2>
            <p className="text-sm text-slate-400">
              {t("twoFactor.challenge.subtitle")}
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-200">
                {t("twoFactor.challenge.codeLabel")}
              </label>
              <input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={8}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\s/g, ""))}
                className="w-full rounded-2xl border border-white/10 bg-slate-950/40 px-4 py-3 text-center text-2xl tracking-[0.5em] text-white placeholder:text-slate-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-cyan-400"
                placeholder="------"
              />
            </div>

            {error && (
              <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || code.length < 6}
              className="inline-flex w-full items-center justify-center rounded-2xl bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-cyan-500/30 transition hover:shadow-cyan-400/50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isLoading ? (
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              ) : (
                t("twoFactor.challenge.submit")
              )}
            </button>

            <p className="text-center text-xs text-slate-400">
              {t("twoFactor.challenge.backupHint")}
            </p>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="text-sm text-cyan-200 hover:text-white"
              >
                {t("twoFactor.challenge.backToLogin")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorChallenge;
